import { useState, useEffect, useCallback } from 'react';
import { FiPrinter, FiX } from 'react-icons/fi';
import API from '../../shared/api';

const esc = (v) =>
  String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

function fmtPrecio(n, simbolo) {
  const v = parseFloat(n);
  if (isNaN(v)) return `${simbolo || '$'}0,00`;
  return `${simbolo || '$'}${v.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function buildListaHtml({ empresa, moneda, conExistencia, productos }) {
  const simbolo = moneda?.simbolo || '$';
  const tasa = moneda ? parseFloat(moneda.tasa_cambio) || 1 : 1;
  const rows = productos
    .map((p) => {
      const precio = parseFloat(p.precio || 0) * tasa;
      const stockCell = conExistencia ? `<td class="stock">${Number(p.stock || 0)}</td>` : '';
      return `<tr><td class="cod">${esc(p.codigo)}</td><td>${esc(p.descripcion)}</td><td class="precio">${esc(fmtPrecio(precio, simbolo))}</td>${stockCell}</tr>`;
    })
    .join('');

  const stockHeader = conExistencia ? '<th class="stock">Exist.</th>' : '';

  return `<!doctype html><html><head><meta charset="utf-8"><title>Lista de precios</title>
<style>
@page { size: A4; margin: 12mm; }
body { font-family: Arial, sans-serif; font-size: 11px; color: #000; }
h1 { font-size: 16px; margin: 0 0 2px; }
.meta { font-size: 11px; margin-bottom: 10px; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 3px 6px; text-align: left; border-bottom: 1px solid #ddd; }
th { border-top: 2px solid #000; border-bottom: 1px solid #000; font-size: 10px; text-transform: uppercase; }
td.cod { font-family: 'Courier New', monospace; width: 80px; }
td.precio { text-align: right; width: 100px; white-space: nowrap; }
td.stock { text-align: right; width: 60px; }
th.stock { text-align: right; }
</style></head><body>
<h1>Lista de precios</h1>
<div class="meta">${esc(empresa || '')} · Moneda: ${esc(moneda?.nombre || '')} (${esc(simbolo)}) · ${productos.length} artículos</div>
<table>
<thead><tr><th class="cod">Código</th><th>Descripción</th><th class="precio">Precio</th>${stockHeader}</tr></thead>
<tbody>${rows}</tbody>
</table>
</body></html>`;
}

export default function ListaPreciosModal({ sede, empresa, onClose }) {
  const [productos, setProductos] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [monedaSel, setMonedaSel] = useState(null);
  const [conExistencia, setConExistencia] = useState(true);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setCargando(true);
    setError(null);
    API.get(`/inventario/imprimir-precios?sede=${encodeURIComponent(sede || 'todas')}`)
      .then((r) => {
        if (!mounted) return;
        const d = r.data?.data || {};
        setProductos(d.productos || []);
        const arr = d.monedas || [];
        setMonedas(arr);
        const principal = arr.find((m) => m.es_principal) || arr[0];
        if (principal) setMonedaSel(principal.codigo);
      })
      .catch((e) => mounted && setError(e.response?.data?.detail || e.message))
      .finally(() => mounted && setCargando(false));
    return () => {
      mounted = false;
    };
  }, [sede]);

  const moneda = monedas.find((m) => m.codigo === monedaSel);

  const imprimir = useCallback(() => {
    const html = buildListaHtml({ empresa, moneda, conExistencia, productos });
    let win = null;
    try {
      win = window.open('', '_blank', 'width=800,height=900');
    } catch {
      win = null;
    }
    if (win && win.document) {
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.addEventListener('load', () => setTimeout(() => { try { win.focus(); win.print(); } catch (e) {} }, 250));
      return;
    }
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:800px;height:900px;border:0;background:#fff;';
    document.body.appendChild(iframe);
    const fdoc = iframe.contentWindow.document;
    fdoc.open();
    fdoc.write(html);
    fdoc.close();
    iframe.addEventListener('load', () => setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} }, 250));
    setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 30000);
  }, [empresa, moneda, conExistencia, productos]);

  const tasa = moneda ? parseFloat(moneda.tasa_cambio) || 1 : 1;

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)', padding: 20,
      }}
    >
      <div
        style={{
          background: '#fff', borderRadius: 18, width: 720, maxWidth: '96%', maxHeight: '92vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8eaef', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#1F2937' }}>Imprimir lista de precios</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{empresa || ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4 }}>
            <FiX size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: 14, padding: '14px 20px', flexWrap: 'wrap', flexShrink: 0 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'flex', flexDirection: 'column', gap: 6 }}>
            Moneda
            <select
              value={monedaSel || ''}
              onChange={(e) => setMonedaSel(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: 13, minWidth: 160 }}
            >
              {monedas.map((m) => (
                <option key={m.id} value={m.codigo}>{m.nombre} ({m.simbolo})</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'flex-end', paddingBottom: 8 }}>
            <input type="checkbox" checked={conExistencia} onChange={(e) => setConExistencia(e.target.checked)} />
            Incluir existencia
          </label>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 14px' }}>
          {cargando && <div style={{ padding: 30, textAlign: 'center', color: '#6B7280' }}>Cargando...</div>}
          {error && <div style={{ padding: 20, color: '#c5221f', fontSize: 13 }}>{error}</div>}
          {!cargando && !error && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #000' }}>
                  <th style={{ textAlign: 'left', padding: '4px 6px', width: 90, fontFamily: 'monospace' }}>Código</th>
                  <th style={{ textAlign: 'left', padding: '4px 6px' }}>Descripción</th>
                  <th style={{ textAlign: 'right', padding: '4px 6px', width: 110 }}>Precio</th>
                  {conExistencia && <th style={{ textAlign: 'right', padding: '4px 6px', width: 70 }}>Exist.</th>}
                </tr>
              </thead>
              <tbody>
                {productos.map((p) => (
                  <tr key={p.codigo} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '3px 6px', fontFamily: 'monospace' }}>{p.codigo}</td>
                    <td style={{ padding: '3px 6px' }}>{p.descripcion}</td>
                    <td style={{ padding: '3px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtPrecio(parseFloat(p.precio || 0) * tasa, moneda?.simbolo)}</td>
                    {conExistencia && <td style={{ padding: '3px 6px', textAlign: 'right' }}>{Number(p.stock || 0)}</td>}
                  </tr>
                ))}
                {productos.length === 0 && (
                  <tr><td colSpan={conExistencia ? 4 : 3} style={{ padding: 30, textAlign: 'center', color: '#6B7280' }}>Sin productos</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid #e8eaef', flexShrink: 0 }}>
          <button
            onClick={imprimir}
            disabled={cargando || !!error || productos.length === 0}
            style={{
              width: '100%', padding: 12, borderRadius: 12, border: 'none', background: '#2563eb',
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              opacity: cargando || !!error || productos.length === 0 ? 0.5 : 1,
            }}
          >
            <FiPrinter size={16} /> Imprimir lista
          </button>
        </div>
      </div>
    </div>
  );
}
